import { useState, useEffect, useCallback } from 'react';
import { Box, Text, Button, Loader } from '@pingux/astro';
import { SecretRequest, SecretRequestStatus } from '@app-types/tap-partner-vault';
import { requestsService } from '@services/requests';

type FilterTab = 'all' | SecretRequestStatus;

const statusBadgeStyle = (status: SecretRequestStatus): React.CSSProperties => {
  const map: Record<SecretRequestStatus, { bg: string; color: string }> = {
    pending:  { bg: '#fef3c7', color: '#92400e' },
    approved: { bg: '#d1fae5', color: '#065f46' },
    denied:   { bg: '#fee2e2', color: '#991b1b' },
  };
  return {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.7rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    ...map[status],
  };
};

const getRequesterDisplayName = (request: SecretRequest): string =>
  request.requester_name?.trim() || 'Unknown user';

const RequestsDashboard: React.FC = () => {
  const [requests, setRequests] = useState<SecretRequest[]>([]);
  const [filter, setFilter] = useState<FilterTab>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewAction, setReviewAction] = useState<'approve' | 'deny' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const status = filter === 'all' ? undefined : filter;
      const data = await requestsService.listRequests(status);
      setRequests(data);
    } catch {
      setError('Failed to load requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const startReview = (id: string, action: 'approve' | 'deny') => {
    setReviewingId(id);
    setReviewAction(action);
    setReviewNote('');
  };

  const cancelReview = () => {
    setReviewingId(null);
    setReviewAction(null);
    setReviewNote('');
  };

  const submitReview = async () => {
    if (!reviewingId || !reviewAction) return;
    try {
      setSubmitting(true);
      if (reviewAction === 'approve') {
        await requestsService.approveRequest(reviewingId, reviewNote || undefined);
      } else {
        await requestsService.denyRequest(reviewingId, reviewNote || undefined);
      }
      cancelReview();
      await loadRequests();
    } catch {
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'pending',  label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'denied',   label: 'Denied' },
    { key: 'all',      label: 'All' },
  ];

  return (
    <Box sx={{ padding: '1.5rem 0' }}>
      <Box sx={{ marginBottom: '1.5rem' }}>
        <Text sx={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>
          Access Requests
        </Text>
        <Text sx={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
          Review and manage secret access requests from users
        </Text>
      </Box>

      {/* Filter tabs */}
      <Box isRow sx={{ gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0' }}>
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: filter === key ? '600' : '400',
              color: filter === key ? '#2563eb' : '#6b7280',
              background: 'none',
              border: 'none',
              borderBottom: filter === key ? '2px solid #2563eb' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: '-1px',
            }}
          >
            {label}
          </button>
        ))}
      </Box>

      {error && (
        <Box sx={{ padding: '1rem', backgroundColor: '#fee2e2', borderRadius: '0.5rem', marginBottom: '1rem', borderLeft: '4px solid #dc2626' }}>
          <Text sx={{ color: '#991b1b' }}>{error}</Text>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Loader />
        </Box>
      ) : requests.length === 0 ? (
        <Box sx={{ padding: '3rem', textAlign: 'center', backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Text sx={{ color: '#6b7280' }}>
            {filter === 'pending' ? 'No pending requests.' : `No ${filter === 'all' ? '' : filter + ' '}requests found.`}
          </Text>
        </Box>
      ) : (
        <Box sx={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['Partner', 'Tenant', 'Type', 'Requester', 'Requested', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <>
                  <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#111827' }}>{req.partner ?? '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>{req.tenant_id ?? '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{req.credential_type?.replace('_', ' ') ?? '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>{getRequesterDisplayName(req)}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDate(req.created_at)}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={statusBadgeStyle(req.status)}>{req.status}</span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {req.status === 'pending' && reviewingId !== req.id && (
                        <Box isRow sx={{ gap: '0.5rem' }}>
                          <Button variant="default" onPress={() => startReview(req.id, 'approve')} sx={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}>
                            Approve
                          </Button>
                          <Button variant="critical" onPress={() => startReview(req.id, 'deny')} sx={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}>
                            Deny
                          </Button>
                        </Box>
                      )}
                      {req.status !== 'pending' && req.review_note && (
                        <Text sx={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>{req.review_note}</Text>
                      )}
                    </td>
                  </tr>
                  {/* Inline review note row */}
                  {reviewingId === req.id && (
                    <tr key={`${req.id}-review`} style={{ backgroundColor: reviewAction === 'approve' ? '#f0fdf4' : '#fef2f2', borderBottom: '1px solid #e5e7eb' }}>
                      <td colSpan={7} style={{ padding: '1rem' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          <Text sx={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>
                            {reviewAction === 'approve' ? 'Approve' : 'Deny'} request from {getRequesterDisplayName(req)}
                          </Text>
                          <textarea
                            placeholder="Optional note to requester…"
                            value={reviewNote}
                            onChange={e => setReviewNote(e.target.value)}
                            rows={2}
                            style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', fontSize: '0.875rem', resize: 'vertical' }}
                          />
                          <Box isRow sx={{ gap: '0.5rem' }}>
                            <Button
                              variant={reviewAction === 'approve' ? 'default' : 'critical'}
                              onPress={submitReview}
                              isLoading={submitting}
                              sx={{ fontSize: '0.875rem' }}
                            >
                              Confirm {reviewAction === 'approve' ? 'Approval' : 'Denial'}
                            </Button>
                            <Button variant="link" onPress={cancelReview} sx={{ fontSize: '0.875rem' }}>
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};

export default RequestsDashboard;
